import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import { useSnackbar } from "../../../components/snackbar/SnackbarProvider";
import {
  createAdminSampleType,
  fetchAdminSampleTypes,
  updateAdminSampleType,
} from "../../../utils/api";

const maroon = "#500000";

const EMPTY_DRAFT = {
  sampleLetter: "",
  shortName: "",
  longName: "",
  volume: "",
  quantity: "",
  container: "",
  site: "",
};

const SampleTypeManagementCard = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageError, setPageError] = useState("");
  const [formError, setFormError] = useState("");
  const { openSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);

  const loadData = async () => {
    try {
      setLoading(true);
      setPageError("");
      const response = await fetchAdminSampleTypes();
      setRows(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error loading sample types:", error);
      const msg = error?.response?.data?.message || error?.message || "Failed to load sample types.";
      setPageError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.sampleLetter);
    setDraft({
      sampleLetter: row.sampleLetter || "",
      shortName: row.shortName || "",
      longName: row.longName || "",
      volume: row.volume || "",
      quantity: row.quantity || "",
      container: row.container || "",
      site: row.site || "",
    });
    setFormError("");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!(draft.sampleLetter || "").trim()) {
      setFormError("Sample letter is required.");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...draft,
        sampleLetter: (draft.sampleLetter || "").trim().toUpperCase(),
        shortName: (draft.shortName || "").trim() || null,
        longName: (draft.longName || "").trim() || null,
        volume: (draft.volume || "").trim() || null,
        quantity: (draft.quantity || "").trim() || null,
        container: (draft.container || "").trim() || null,
        site: (draft.site || "").trim() || null,
      };

      if (editingId) {
        await updateAdminSampleType(editingId, payload);
        openSnackbar("Sample type updated successfully!", "success");
      } else {
        await createAdminSampleType(payload);
        openSnackbar("Sample type created successfully!", "success");
      }

      setDialogOpen(false);
      setDraft(EMPTY_DRAFT);
      await loadData();
    } catch (error) {
      console.error("Error saving sample type:", error);
      const msg = error?.response?.data?.message || error?.message || "Failed to save sample type.";
      setFormError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card elevation={1} sx={{ boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25)", borderRadius: 0 }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: maroon }}>
              Sample Type Management
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="text"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{
                color: "#666",
                fontWeight: 600,
                fontSize: "0.75rem",
                py: 0.5,
                px: 1.5,
                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)", color: "#333" },
              }}
              data-tour-id="tour-add-sample-type"
            >
              ADD SAMPLE TYPE
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {pageError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {pageError}
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "220px minmax(0, 1fr)" },
            gap: 3,
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 2,
              minHeight: 84,
              textAlign: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: maroon, fontWeight: 700, fontSize: "0.8rem" }}
            >
              TOTAL SAMPLE TYPES
            </Typography>
            <Typography variant="h3" sx={{ color: maroon, fontWeight: 700 }}>
              {rows.length}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0, pr: { xs: 0, md: 4 } }}>
            <Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "80px minmax(120px, 1fr) 80px 80px 96px",
                  gap: 2,
                  pb: 1,
                }}
              >
                {["TYPE", "NAME", "VOL", "QTY", "ACTION"].map((header, index) => (
                  <Typography
                    key={header}
                    variant="caption"
                    sx={{
                      color: "#666",
                      fontWeight: 700,
                      textAlign: index === 4 ? "right" : "left",
                    }}
                  >
                    {header}
                  </Typography>
                ))}
              </Box>

              {loading ? (
                <Box sx={{ py: 5, textAlign: "center", color: "#666" }}>
                  Loading sample types...
                </Box>
              ) : rows.length === 0 ? (
                <Box sx={{ py: 5, textAlign: "center", color: "#666" }}>
                  No sample types found.
                </Box>
              ) : (
                <List sx={{ py: 0 }}>
                  {rows.map((row, index) => (
                    <ListItem
                      key={row.sampleLetter}
                      sx={{
                        px: 0,
                        py: 0.75,
                        display: "grid",
                        gridTemplateColumns: "80px minmax(120px, 1fr) 80px 80px 96px",
                        gap: 2,
                        borderTop: index === 0 ? "1px solid #eee" : 0,
                        borderBottom: index === rows.length - 1 ? 0 : "1px solid #eee",
                        alignItems: "center",
                      }}
                    >
                      <ListItemText
                        primary={row.sampleLetter}
                        primaryTypographyProps={{ fontSize: "0.9rem", fontWeight: 500 }}
                        sx={{ m: 0 }}
                      />
                      <Typography variant="body2">{row.longName || "—"}</Typography>
                      <Typography variant="body2">{row.volume || "—"}</Typography>
                      <Typography variant="body2">{row.quantity || "—"}</Typography>
                      <Box sx={{ textAlign: "right" }}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => openEdit(row)}
                            sx={{ color: maroon }}
                            data-tour-id={index === 0 ? "tour-edit-sample-type" : undefined}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: maroon, color: "white" }}>
          {editingId ? "Edit Sample Type" : "Create New Sample Type"}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Box
            sx={{
              mt: 1,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
              gap: 2,
            }}
          >
            {[
              ["sampleLetter", "Sample Letter"],
              ["shortName", "Short Name"],
              ["longName", "Long Name"],
              ["volume", "Volume"],
              ["quantity", "Quantity"],
              ["container", "Container"],
              ["site", "Site"],
            ].map(([field, label]) => (
              <TextField
                key={field}
                label={label}
                value={draft[field]}
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, [field]: event.target.value }))
                }
                fullWidth
                required={field === "sampleLetter"}
                disabled={saving || (editingId && field === "sampleLetter")}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => { setDialogOpen(false); setFormError(""); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ backgroundColor: maroon, "&:hover": { backgroundColor: "#4a0000" } }}
          >
            {saving ? "Saving..." : editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default SampleTypeManagementCard;
